/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { ID3Tag } from '../ID3Tag';
import { Logger } from '@quickplay/foundation';
import { MetadataInformation, MetaTag } from '@quickplay/player-interface';

const SCHEME_ID_URI = 'urn:yospace:a:id3:2016';

export class LiveSessionHandler {
  /**
   * The ID3 tag being built.
   * @property {any} id3
   */
  private id3: any;
  /**
   * Provides access to the logger.
   * @property {Logger} logger
   */
  logger: Logger;
  /**
   * Provides access to the Yospace SDK.
   * @property {any} yospaceSDK
   */
  private yospaceSDK: any;
  /**
   * Sets the Yospace SDK.
   * @param yospaceSDK
   */

  constructor(yospaceSDK: any, logger: Logger) {
    this.logger = logger;
    this.yospaceSDK = yospaceSDK;
  }

  /**
   * Creates a Yospace timed metadata object. This works for Live emsg metadata only
   * @param metadataInfo the metadata information to parse
   * @param playheadPosition the current playhead position
   */
  createTimedMetadataFromEmsgEvent(metadataInfo: MetadataInformation, playheadPosition: number): any {
    let ymid!: string;
    let yseq!: string;
    let ytyp!: string;
    let ydur!: string;

    metadataInfo.metaTags.forEach((metaTag: MetaTag) => {
      this.logger.log('metaTag', metaTag);

      if (metaTag.schemeIdUri.toUpperCase() === SCHEME_ID_URI.toUpperCase()) {
        const emsgData = this.bytesToString(metaTag.extendedAttributes);

        this.logger.log('emsgData', emsgData);

        const frames = emsgData.split(',');

        frames.forEach((frame: string) => {
          const tokens = frame.split('=');

          switch (tokens[0]) {
            case ID3Tag.YMID: {
              ymid = tokens[1];
              break;
            }
            case ID3Tag.YSEQ: {
              yseq = tokens[1];
              break;
            }
            case ID3Tag.YTYP: {
              ytyp = tokens[1];
              break;
            }
            case ID3Tag.YDUR: {
              ydur = tokens[1];
              break;
            }
          }
        });
      }
    });

    if (ymid !== undefined && yseq !== undefined && ytyp !== undefined && ydur !== undefined) {
      return this.yospaceSDK.TimedMetadata.createFromMetadata(ymid, yseq, ytyp, ydur, playheadPosition);
    }

    return null;
  }

  /**
   * Updates the ID3 tag and if it is completed then return a timed metadata object. This works for Vod metadata only.
   * @param metadataEvent
   * @param playheadPosition
   */
  createTimedMetadataFromMetadataEvent(metadataEvent: any, playheadPosition: number): any {
    const payload = metadataEvent.payload;

    this.logger.log('ID3 Tag Metadata', metadataEvent);
    this.logger.log('ID3 Tag Metadata payload', metadataEvent.payload);
    // verify if this is a YMID, YSEQ, YTYP or YDUR metadata
    if (
      payload.key === ID3Tag.YMID ||
      payload.key === ID3Tag.YSEQ ||
      payload.key === ID3Tag.YTYP ||
      payload.key === ID3Tag.YDUR
    ) {
      this.processID3Data(metadataEvent, playheadPosition);
    } else {
      this.handleInvalidMetadata(metadataEvent);
    }

    return null;
  }

  /**
   * Creates a string from an array of bytes.
   * @param data the array of bytes
   * @param startingIndex the start index to read from. For type "metadata" the starting index must be 1.
   */
  private bytesToString(data: number[], startingIndex = 0): string {
    let str = '';

    for (let i = startingIndex; i < data.length; i++) {
      str += String.fromCharCode(data[i]);
    }

    return str;
  }

  private setID3Tag(metadataEvent: any): any {
    this.id3 = {
      startTime: metadataEvent.startTime,
    };
  }

  private processID3Data(metadataEvent: any, playheadPosition: number): any {
    const payload = metadataEvent.payload;
    if (this.id3 && this.id3.hasOwnProperty('startTime')) {
      if (this.id3.startTime !== metadataEvent.startTime) {
        // this is a new cue (signal)
        // all ID3 required fields are set
        if (
          this.id3.YMID !== undefined &&
          this.id3.YSEQ !== undefined &&
          this.id3.YTYP !== undefined &&
          this.id3.YDUR !== undefined
        ) {
          return this.createId3Metadata(metadataEvent, playheadPosition);
        } else {
          this.logger.warn('The last ID3 tag could not be completed', metadataEvent);
          // reset id3 tag
          this.setID3Tag(metadataEvent);
        }
      }
    } else {
      // create a new id3 tag
      this.setID3Tag(metadataEvent);
    }

    // set the ID3 field related to this cue
    this.id3[payload.key] = this.bytesToString(payload.data, 1);
  }

  private createId3Metadata(metadataEvent: any, playheadPosition: number): any {
    const metadata = this.yospaceSDK.TimedMetadata.createFromMetadata(
      this.id3.YMID,
      this.id3.YSEQ,
      this.id3.YTYP,
      this.id3.YDUR,
      playheadPosition,
    );

    this.logger.log(`ID3 tag`, this.id3);

    // reset id3 tag
    this.setID3Tag(metadataEvent);

    // set the ID3 field related to this cue
    this.id3[metadataEvent.payload.key] = this.bytesToString(metadataEvent.payload.data, 1);

    return metadata;
  }

  private handleInvalidMetadata(metadataEvent: any): void {
    // In this case, CMAF Payload key is X-COM-YOSPACE-YMID or X-COM-YOSPACE-NEXTINF.

    // HTTP Live Streaming (HLS) allows for delivery of timed metadata events, both in-band and out-of-band:
    // HLS supports delivery of ID3 timed metadata carried in-band within MPEG-2
    // Transport Streams [HLS-TIMED-METADATA]. ID3 metadata is stored as a complete ID3v2.4
    // frame in an elementary stream (PES) packet, including a complete ID3 header [ID3v2].
    // Out-of-band events are delivered using the EXT-X-DATERANGE tag in a HLS playlist file.

    // But the safari browser does not parse/access the Out-of-band events with EXT-X-DATERANGE tag properly.
    // Though the ad and playback work on the safari, required analytics data such as id, startTime
    // is not available in the safari browser.

    // Links:
    // 1. https://developer.apple.com/forums/thread/669903
    // 2. https://datatracker.ietf.org/doc/html/draft-pantos-hls-rfc8216bis-04#section-4.4.2.7

    this.logger.warn('ID3 Tag Metadata may contain EXT-X-DATERANGE tag');
    this.logger.warn('ID3 Tag Metadata are not processed properly', metadataEvent);
  }
}
